import React from "react";
import { Box, Typography, Container, Divider } from "@mui/material";
import PrivacyDataTable from "./PrivacyDataTable";
import SectionWrapper from "../../components/wrappers/section";

const PrivacyPolicyPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h3" fontWeight={700} gutterBottom>
        FYMAB Inc. Privacy Policy
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Effective: August 18, 2025 • Last Updated: August 18, 2025
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* INTRODUCTION */}
      <SectionWrapper title="Introduction">
        <Typography paragraph>
          This Privacy Policy for FYMAB Inc. ("we," "us," or "our") describes
          how and why we might collect, store, use, and/or share ("process")
          your personal information when you use our services ("Services", or
          “Mindsherpa”).
        </Typography>

        <Typography paragraph>This policy applies when you:</Typography>

        <ul>
          <li>
            Visit our website at{" "}
            <a href="https://www.mindsherpa.health/" target="_blank">
              https://www.mindsherpa.health/
            </a>
            , or any of our other websites that link to this Privacy Policy.
          </li>
          <li>
            Download and use the Mindsherpa mobile application, an employee mind
            and body wellness platform.
          </li>
          <li>
            Engage with us in other related ways, including sales, marketing, or
            events.
          </li>
        </ul>

        <Typography paragraph>
          Reading this Privacy Policy will help you understand your privacy
          rights and choices. We are dedicated to protecting your personal
          information and your right to privacy. If you do not agree with our
          policies and practices, please do not use our Services. If you still
          have any questions or concerns, please contact our Privacy Officer at{" "}
          <strong>privacy@mindsherpa.health</strong>.
        </Typography>
      </SectionWrapper>

      {/* CONTENTS */}
      <SectionWrapper title="Contents">
        <ol>
          <li>Summary of Key Points</li>
          <li>What Information Do We Collect?</li>
          <li>How Do We Process Your Information?</li>
          <li>Legal Bases for Processing Your Information</li>
          <li>When and With Whom Do We Share Your Personal Information?</li>
          <li>Data Security: How We Protect Your Information</li>
          <li>Data Retention</li>
          <li>Information from Minors</li>
          <li>Your Canadian Privacy Rights (PIPEDA & Provincial Laws)</li>
          <li>Your United States Privacy Rights (HIPAA & State Laws)</li>
          <li>Controls for Do-Not-Track Features</li>
          <li>Updates to This Policy</li>
          <li>How to Contact Us</li>
        </ol>
      </SectionWrapper>

      {/* SUMMARY OF KEY POINTS */}
      <SectionWrapper title="1. Summary of Key Points">
        <Typography paragraph>
          This summary provides key points from our Privacy Policy. You can find
          more details by using the table of contents to navigate to the
          relevant sections.
        </Typography>

        <ul>
          <li>
            <strong>What personal information do we process?</strong> We process
            personal, usage, and sensitive health-related information depending
            on how you interact with our Services.
          </li>
          <li>
            <strong>Do we process sensitive personal information?</strong> Yes.
            As a health and wellness application, we process sensitive data,
            including health information. In the United States, this data may be
            considered Protected Health Information (PHI) under HIPAA. We
            process this information with your explicit consent and in
            accordance with applicable laws.
          </li>
          <li>
            <strong>How do we process your information?</strong> We process your
            information to provide, improve, and administer our Services,
            communicate with you, for security and fraud prevention, and to
            comply with the law. We do not share your individual personal data
            with your employer. Reports provided to employers contain only
            aggregated and anonymized data.
          </li>
          <li>
            <strong>With whom do we share personal information?</strong> We may
            share information with third-party service providers who help us
            operate our Services, when required by law, or as part of a business
            transfer. We do not sell your personal information.
          </li>
          <li>
            <strong>How do we keep your information safe?</strong> We use a
            combination of organizational and technical security measures,
            including encryption and access controls, to protect your personal
            information, in line with industry best practices and legal
            requirements like HIPAA.
          </li>
          <li>
            <strong>What are your rights?</strong> Depending on your
            geographical location, you have specific rights regarding your
            personal information under laws like Canada's PIPEDA and various US
            state and federal laws, including HIPAA.
          </li>
          <li>
            <strong>How can you exercise your rights?</strong> You can exercise
            your rights by contacting us at info@mindsherpa.health. We will
            respond to all requests in accordance with applicable data
            protection laws.
          </li>
        </ul>
      </SectionWrapper>

      {/* INFORMATION WE COLLECT */}
      <SectionWrapper title="2. What Information Do We Collect?">
        <Typography paragraph>
          We collect Personal Data when you visit our website, download our
          mobile application, or use our Services. The chart below details the
          categories of Personal Data we may collect.
        </Typography>

        <PrivacyDataTable />
      </SectionWrapper>

      {/* PROCESSING INFORMATION */}
      <SectionWrapper title="3. How Do We Process Your Information?">
        <Typography paragraph>
          We process your information for the following reasons:
        </Typography>

        <ul>
          <li>
            To provide and manage the Services, including account creation and
            functionality.
          </li>
          <li>
            To personalize your experience, including wellness recommendations.
          </li>
          <li>
            To communicate with you via email, notifications, and support.
          </li>
          <li>
            To provide anonymized analytics to your employer (never identifiable
            data).
          </li>
          <li>For security, fraud prevention, and platform protection.</li>
          <li>To comply with legal obligations and regulatory requirements.</li>
          <li>To analyze usage and improve our Services.</li>
        </ul>
      </SectionWrapper>

      {/* LEGAL BASES */}
      <SectionWrapper title="4. Legal Bases for Processing Your Information">
        <ul>
          <li>
            <strong>Consent:</strong> Required for sensitive health data.
          </li>
          <li>
            <strong>Contractual obligations:</strong> To deliver the Services
            you signed up for.
          </li>
          <li>
            <strong>Legitimate interests:</strong> Improving services and
            security.
          </li>
          <li>
            <strong>Legal obligations:</strong> Compliance with laws and
            authorities.
          </li>
        </ul>
      </SectionWrapper>

      {/* SHARING INFORMATION */}
      <SectionWrapper title="5. When and With Whom Do We Share Your Personal Information?">
        <ul>
          <li>
            <strong>Third-Party Service Providers:</strong> Cloud hosting,
            analytics, payments.
          </li>
          <li>
            <strong>Employers:</strong> Only aggregated and anonymized reports.
            No personal data.
          </li>
          <li>
            <strong>Business Transfers:</strong> Mergers, acquisitions, etc.
          </li>
          <li>
            <strong>Legal Requirements:</strong> Court orders, law enforcement.
          </li>
        </ul>
      </SectionWrapper>

      {/* DATA SECURITY */}
      <SectionWrapper title="6. Data Security: How We Protect Your Information">
        <Typography>
          Our measures include encryption, access controls, audits, employee
          training, and breach notification procedures. No system is 100%
          secure, but we follow industry best practices and HIPAA standards.
        </Typography>

        <ul>
          <li>
            <strong>Encryption:</strong> All data, including Protected Health
            Information (PHI), is encrypted both in transit (using TLS) and at
            rest.
          </li>

          <li>
            <strong>Access Control:</strong> Access to personal information is
            strictly limited to authorized personnel who have a legitimate need
            to access it to perform their job functions.
          </li>

          <li>
            <strong>Regular Audits:</strong> We conduct regular security risk
            assessments and vulnerability scanning to identify and remediate
            potential threats.
          </li>

          <li>
            <strong>Employee Training:</strong> Our employees receive regular
            training on data privacy and security protocols.
          </li>

          <li>
            <strong>Data Breach Notification:</strong> In the event of a data
            breach, we will notify affected individuals and authorities as
            required by applicable laws, including PIPEDA and HIPAA.
          </li>
        </ul>

        <Typography>
          However, no electronic transmission over the internet or information
          storage technology can be guaranteed to be 100% secure. While we will
          do our best to protect your personal information, transmission of
          personal information to and from our Services is at your own risk.
        </Typography>
      </SectionWrapper>

      {/* DATA RETENTION */}
      <SectionWrapper title="7. Data Retention">
        <Typography component="div">
          We retain your personal information for as long as it is necessary to
          provide the Services to you and to fulfill the purposes outlined in
          this Privacy Policy. We have specific data retention policies that
          dictate how long we hold onto information. For example, we generally
          delete or anonymize personal information within{" "}
          <strong>90 days</strong> of a user's account termination, unless a
          longer retention period is required by law (for example, for tax,
          legal, or accounting purposes).
        </Typography>
      </SectionWrapper>

      {/* MINORS */}
      <SectionWrapper title="8. Information from Minors">
        <Typography paragraph>
          Our Services are not intended for individuals under 18. If such data
          is discovered, it is deleted immediately.
        </Typography>
      </SectionWrapper>

      {/* CANADA RIGHTS */}
      <SectionWrapper title="9. Your Canadian Privacy Rights (PIPEDA & Provincial Laws)">
        <Typography>
          If you are a resident of Canada, you have specific rights regarding
          your personal information under the Personal Information Protection
          and Electronic Documents Act (PIPEDA) and applicable provincial
          privacy legislation, such as Alberta's and British Columbia's PIPA and
          Quebec's Act respecting the protection of personal information in the
          private sector (Law 25). These rights include:
        </Typography>

        <ul>
          <li>
            <strong>The Right to Access:</strong> You have the right to request
            access to the personal information we hold about you.
          </li>

          <li>
            <strong>The Right to Correction (Rectification):</strong> You have
            the right to request the correction of inaccurate or incomplete
            personal information.
          </li>

          <li>
            <strong>The Right to Withdraw Consent:</strong> You have the right
            to withdraw your consent to our processing of your personal
            information at any time, subject to legal or contractual
            restrictions.
          </li>
        </ul>

        <Typography>
          To exercise these rights, please contact our Privacy Officer.
        </Typography>
      </SectionWrapper>

      {/* US RIGHTS */}
      <SectionWrapper title="10. Your United States Privacy Rights (HIPAA & State Laws)">
        <Typography>
          If you are a resident of the United States, your rights are governed
          by various federal and state laws.
        </Typography>

        <Typography>
          <strong>
            Health Insurance Portability and Accountability Act (HIPAA)
          </strong>
        </Typography>

        <Typography>
          Because our Services involve the processing of health information and
          are offered through employer-sponsored wellness programs, we are
          considered a "Business Associate" under HIPAA. We are legally
          obligated to protect your "Protected Health Information" (PHI) in
          accordance with HIPAA's requirements. Your rights regarding your PHI
          under HIPAA include:
        </Typography>

        <ul>
          <li>
            <strong>The Right to Access:</strong> You can request a copy of your
            PHI that we maintain.
          </li>

          <li>
            <strong>The Right to Amend:</strong> You can request that we amend
            any inaccurate or incomplete PHI.
          </li>

          <li>
            <strong>The Right to an Accounting of Disclosures:</strong> You can
            request an accounting of certain disclosures we have made of your
            PHI.
          </li>
        </ul>

        <Typography>
          <strong>State-Specific Privacy Rights (e.g., CCPA/CPRA)</strong>
        </Typography>

        <Typography>
          Residents of states like California, Virginia, Colorado, and others
          have additional rights, which may include:
        </Typography>

        <ul>
          <li>
            The right to know what personal information is being collected.
          </li>

          <li>The right to delete personal information.</li>

          <li>
            The right to opt-out of the "sale" or "sharing" of personal
            information (for the record, we do not sell your personal
            information).
          </li>

          <li>
            The right to non-discrimination for exercising your privacy rights.
          </li>
        </ul>

        <Typography>
          To exercise any of your US privacy rights, please contact our Privacy
          Officer.
        </Typography>
      </SectionWrapper>

      {/* COOKIES */}
      <SectionWrapper title="11. Controls for Do-Not-Track Features & Cookies">
        <Typography>
          <strong>Do-Not-Track:</strong> Most web browsers and some mobile
          operating systems include a Do-Not-Track ("DNT") feature or setting
          you can activate to signal your privacy preference not to have data
          about your online browsing activities monitored and collected. At this
          stage, no uniform technology standard for recognizing and implementing
          DNT signals has been finalized. As such, we do not currently respond
          to DNT browser signals or any other mechanism that automatically
          communicates your choice not to be tracked online.
        </Typography>
        <Typography>
          <strong>Cookies and Other Tracking Technologies:</strong> We use
          cookies and similar tracking technologies to collect and store your
          information. This helps us provide and improve our Services and
          remember your preferences. You can typically manage your cookie
          preferences through your web browser's settings. However, disabling
          certain cookies may affect the functionality of our Services.
        </Typography>
      </SectionWrapper>

      {/* UPDATES */}
      <SectionWrapper title="12. Updates to This Policy">
        <Typography paragraph>
          We may update this Privacy Policy from time to time. The updated
          version will be indicated by a "Last Updated" date at the top of this
          policy. If we make material changes, we may notify you either by
          prominently posting a notice of such changes or by directly sending
          you a notification. We encourage you to review this Privacy Policy
          frequently to stay informed of how we are protecting your information.
        </Typography>
      </SectionWrapper>

      {/* CONTACT */}
      <SectionWrapper title="13. How to Contact Us">
        <Typography>
          If you have questions, comments, or wish to exercise your privacy
          rights, you may contact our Privacy Officer by email or post:
          <strong>Privacy Officer</strong>
          <br />
          FYMAB Inc.
          <br />
          181 Greenbank Rd, BOX 30047
          <br />
          Ottawa, ON, K2H 5V0
          <br />
          Canada
          <br />
          Email:{" "}
          <strong>
            <a href="mailto:privacy@mindsherpa.health">
              privacy@mindsherpa.health
            </a>
          </strong>
        </Typography>
      </SectionWrapper>

      <Box height={50} />
    </Container>
  );
};

export default PrivacyPolicyPage;
